import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { MapPin, Calendar } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { SsoButtons } from '@/components/auth/SsoButtons';
import { useT, useLanguage, type Lang } from '@/i18n/LanguageContext';
import { getEventBySlug, getEventDisplay } from '@/data/eventRegistry';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const loginWithPassword = useAuthStore((s) => s.loginWithPassword);
  const loginWithSsoToken = useAuthStore((s) => s.loginWithSsoToken);
  const t = useT();
  const { lang, setLang } = useLanguage();

  // Read event slug from URL — only show event details if explicitly provided
  const eventSlug = searchParams.get('event');
  const registryEvent = eventSlug ? getEventBySlug(eventSlug) : undefined;
  const eventDisplay = registryEvent ? getEventDisplay(registryEvent, lang) : null;

  // Persist slug so post-signup auto-select can use it
  if (eventSlug) {
    localStorage.setItem('reevol-event-slug', eventSlug);
  }

  // Handle SSO token from WeChat callback redirect
  useEffect(() => {
    const ssoToken = searchParams.get('sso_token');
    if (ssoToken) {
      loginWithSsoToken(ssoToken);
      // Remove sso_token from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('sso_token');
      setSearchParams(newParams, { replace: true });
      navigate('/', { replace: true });
    }
  }, [searchParams]);

  // Handle error from WeChat callback
  const wechatError = searchParams.get('error');

  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(wechatError ? 'SSO sign-in failed. Please try again.' : '');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password.trim()) {
      setError(t.login.emailPasswordRequired);
      return;
    }
    setLoading(true);
    const result = await loginWithPassword(email.trim(), password.trim());
    setLoading(false);
    if (result === true) {
      navigate('/', { replace: true });
    } else {
      setError(result);
    }
  };

  return (
    <AuthLayout>
      {/* Language switcher */}
      <div className="absolute top-4 right-4 flex items-center gap-1 text-sm">
        {(['en', 'zh'] as Lang[]).map((l) => (
          <button
            key={l}
            onClick={() => setLang(l)}
            className={`px-2.5 py-1 rounded-lg transition-colors ${
              lang === l
                ? 'bg-[#523EF7]/10 text-[#523EF7] font-medium'
                : 'text-[#9A9A9A] hover:text-[#3F3F3F]'
            }`}
          >
            {t.lang[l]}
          </button>
        ))}
      </div>

      {/* Label */}
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#523EF7] mb-4">
        {t.common.appName}
      </p>

      {/* Conference Name or Generic Headline */}
      <h1 className="text-3xl sm:text-4xl font-bold text-[#222222] tracking-tight leading-tight mb-6">
        {eventDisplay?.name ?? t.login.genericHeadline}
      </h1>

      {/* Location & Date — only shown for specific events */}
      {eventDisplay && (
        <div className="flex flex-col items-center gap-2 mb-8 text-[#3F3F3F]">
          <div className="flex items-center gap-2 text-base">
            <MapPin size={18} className="text-[#9A9A9A]" />
            <span>{eventDisplay.location}</span>
          </div>
          <div className="flex items-center gap-2 text-base">
            <Calendar size={18} className="text-[#9A9A9A]" />
            <span>{eventDisplay.dates}</span>
          </div>
        </div>
      )}

      {!showForm ? (
        <>
          {/* Description */}
          <p className="text-[#3F3F3F] text-base leading-relaxed max-w-sm mx-auto mb-10">
            {eventDisplay?.description ?? t.login.genericDescription}
          </p>

          {/* SSO Buttons */}
          <div className="max-w-sm mx-auto mb-4">
            <SsoButtons onError={(msg) => setError(msg)} />
          </div>

          {/* CTA Button — email/password sign-in */}
          <button
            onClick={() => setShowForm(true)}
            className="w-full max-w-sm mx-auto block py-4 px-8 bg-[#523EF7] text-white text-lg font-semibold rounded-full
              hover:bg-[#3D2DD4] transition-all duration-200 hover:-translate-y-0.5
              shadow-[0_4px_16px_rgba(82,62,247,0.3)]"
          >
            {t.login.cta}
          </button>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2 mt-4 max-w-sm mx-auto">{error}</p>
          )}
        </>
      ) : (
        /* Login Form */
        <div className="mt-2 max-w-sm mx-auto">
          {/* SSO Buttons above form */}
          <SsoButtons onError={(msg) => setError(msg)} />

          <form onSubmit={handleLogin} className="space-y-4 text-left">
            <div className="flex flex-col gap-1">
              <label htmlFor="email" className="text-sm font-medium text-[#3F3F3F]">
                {t.login.emailLabel} <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                placeholder={t.login.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="auth-input"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="password" className="text-sm font-medium text-[#3F3F3F]">
                {t.login.passwordLabel} <span className="text-red-500">*</span>
              </label>
              <input
                id="password"
                type="password"
                placeholder={t.login.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="auth-input"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="auth-btn-primary w-full"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 mx-auto" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                t.login.signIn
              )}
            </button>

            <div className="text-center space-y-2 pt-1">
              <Link
                to="/forgot-password"
                className="text-sm text-[#9A9A9A] hover:text-[#523EF7] transition-colors block"
              >
                {t.login.forgotPassword}
              </Link>
              <Link
                to="/signup"
                className="text-sm text-[#9A9A9A] hover:text-[#523EF7] transition-colors block"
              >
                {t.login.createAccount}
              </Link>
            </div>
          </form>
        </div>
      )}
    </AuthLayout>
  );
}
